import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useWaitTimes, PARKS_CONFIG } from './WaitTimesContext';
import { trackAlertFired } from '../analytics';

const NotificationAlertsContext = createContext();

const STORAGE_KEY = 'dlp-ride-alerts';
const COOLDOWN_MS = 5 * 60 * 1000; // 5 min cooldown between repeat notifications for same ride

export function useNotificationAlerts() {
  return useContext(NotificationAlertsContext);
}

export function NotificationAlertsProvider({ children }) {
  const { parksData } = useWaitTimes();

  const [alerts, setAlerts] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );

  const lastNotified = useRef({});

  // Persist alerts to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(alerts));
    } catch {
      // localStorage full — ignore
    }
  }, [alerts]);

  // Sync permission state (e.g. if user changes it in browser settings)
  useEffect(() => {
    if (typeof Notification !== 'undefined') {
      setPermission(Notification.permission);
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return 'denied';
    if (Notification.permission === 'granted') {
      setPermission('granted');
      return 'granted';
    }
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result;
    } catch {
      return 'denied';
    }
  }, []);

  const addAlert = useCallback(async (rideId, rideName, threshold) => {
    // Request permission if not yet granted
    let perm = permission;
    if (perm !== 'granted') {
      perm = await requestPermission();
    }
    if (perm === 'denied') return false;

    setAlerts(prev => ({
      ...prev,
      [rideId]: { rideName, threshold },
    }));
    return true;
  }, [permission, requestPermission]);

  const removeAlert = useCallback((rideId) => {
    setAlerts(prev => {
      const next = { ...prev };
      delete next[rideId];
      return next;
    });
    delete lastNotified.current[rideId];
  }, []);

  const getAlert = useCallback((rideId) => {
    return alerts[rideId] || null;
  }, [alerts]);

  const clearAllAlerts = useCallback(() => {
    setAlerts({});
    lastNotified.current = {};
  }, []);

  // ---- Core: check alerts whenever parksData changes ----
  useEffect(() => {
    if (Object.keys(alerts).length === 0) return;
    if (Object.keys(parksData).length === 0) return;
    if (permission !== 'granted') return;

    // Flat lookup of all attractions by ID
    const allAttractions = {};
    Object.values(parksData).forEach(park => {
      if (park?.liveData) {
        park.liveData.forEach(entity => {
          if (entity.entityType === 'ATTRACTION') {
            allAttractions[entity.id] = entity;
          }
        });
      }
    });

    Object.entries(alerts).forEach(([rideId, alert]) => {
      const attraction = allAttractions[rideId];
      if (!attraction) return;
      if (attraction.status !== 'OPERATING') return;

      const waitTime = attraction.queue?.STANDBY?.waitTime;
      if (waitTime === null || waitTime === undefined) return;

      if (waitTime <= alert.threshold) {
        // Cooldown: don't spam the same ride
        const now = Date.now();
        const lastTime = lastNotified.current[rideId] || 0;
        if (now - lastTime < COOLDOWN_MS) return;

        lastNotified.current[rideId] = now;
        trackAlertFired(alert.rideName, waitTime, alert.threshold);

        const body = waitTime === 0
          ? `${alert.rideName} is Walk On! 🏃‍♂️`
          : `${alert.rideName} dropped to ${waitTime} min (your alert: ≤${alert.threshold} min)`;

        // Prefer SW notification (works in background), fall back to Notification API
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.ready.then(reg => {
            reg.showNotification('🏰 DLP Tracker', {
              body,
              icon: '/icons/icon-192.png',
              badge: '/icons/icon-192.png',
              tag: `wait-alert-${rideId}`,
              renotify: true,
              vibrate: [200, 100, 200],
              data: { url: '/' },
            });
          }).catch(() => {
            new Notification('🏰 DLP Tracker', { body, icon: '/icons/icon-192.png' });
          });
        } else {
          new Notification('🏰 DLP Tracker', { body, icon: '/icons/icon-192.png' });
        }
      }
    });
  }, [parksData, alerts, permission]);

  return (
    <NotificationAlertsContext.Provider value={{
      alerts,
      permission,
      addAlert,
      removeAlert,
      getAlert,
      clearAllAlerts,
      requestPermission,
      alertCount: Object.keys(alerts).length,
    }}>
      {children}
    </NotificationAlertsContext.Provider>
  );
}
